import type { CabinetCard, CabinetRoom } from '../api/types';

/**
 * Слова раздела «Кабинеты» — отдельно от разметки.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОТОМУ ЧТО ИМЕННО ЗДЕСЬ ОШИБАЮТСЯ, А ПРОВЕРИТЬ РАЗМЕТКУ НЕЧЕМ.
 *
 * «Подключено 1 кабинетов из 5» — не описка, а обычный итог склейки
 * строки в JSX. Числа в русском языке требуют трёх форм, и написанные
 * по месту они однажды разойдутся. Здесь их можно проверить настоящими
 * проверками, а не разбором исходника.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** «1 кабинет», «2 кабинета», «5 кабинетов». */
export function cabinetsText(n: number): string {
  const last = n % 10;
  const teen = n % 100 >= 11 && n % 100 <= 14;
  if (!teen && last === 1) return `${n} кабинет`;
  if (!teen && last >= 2 && last <= 4) return `${n} кабинета`;
  return `${n} кабинетов`;
}

/**
 * Строка о месте в тарифе.
 *
 * БЕЗ ПОТОЛКА ГОВОРИМ ТОЛЬКО СКОЛЬКО ПОДКЛЮЧЕНО. «Подключено 3 из ∞» —
 * это не сведение, а знак бесконечности, который человек будет
 * разглядывать вместо того, чтобы прочитать число.
 */
export function roomText(room: CabinetRoom): string {
  const plan = room.planName === null ? '' : ` по тарифу «${room.planName}»`;
  if (room.limit === null) return `Подключено ${cabinetsText(room.used)}${plan}`;
  return `Подключено ${room.used} из ${room.limit}${plan}`;
}

export interface Look {
  text: string;
  wash: string;
  ink: string;
}

/**
 * Что и каким цветом сказать про ключ.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * КОГДА ВСЁ ХОРОШО — НЕ ГОВОРИМ НИЧЕГО.
 *
 * Строка «с ключом всё в порядке» под каждым кабинетом — это строка,
 * которую перестают читать через неделю. А вместе с ней перестают
 * читать и ту единственную, ради которой всё затевалось: «ключ отозван,
 * биддер стоит».
 * ═══════════════════════════════════════════════════════════════════════
 *
 * ТРИ ЦВЕТА, А НЕ ДВА. «Скоро кончится» — это ещё работает, и красный
 * тут врал бы: человек побежит менять ключ, решив, что уже сломалось.
 */
export function tokenLook(token: CabinetCard['token']): Look | null {
  if (token === null) return null;
  if (token.health === 'ok') return null;
  if (token.text === null) return null;

  const soon = token.health === 'soon';
  return {
    text: token.text,
    wash: soon ? 'var(--warn-wash)' : 'var(--bad-wash)',
    ink: soon ? 'var(--warn-ink)' : 'var(--bad-ink)',
  };
}

/**
 * Имя кабинета для показа: своё название, иначе марка, иначе номер.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПЕРЕЕХАЛО ИЗ `layout/shell.tsx`, ГДЕ ЛЕЖАЛО МЕСТНОЙ ФУНКЦИЕЙ.
 *
 * Пока имя кабинета печаталось в одной шапке, местная функция была
 * уместна. Пакет 25.08 завёл выпадение со списком кабинетов, и то же
 * имя понадобилось второму месту. Второй такой же расчёт разошёлся бы
 * с первым на первой правке запасных вариантов — и один кабинет
 * назывался бы в шапке «MEDUZA», а в списке «Продавец 1234567».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function cabinetName(
  displayName: string | null,
  tradeMark: string | null,
  wbOwnerId: number | null,
): string {
  if (displayName) return displayName;
  if (tradeMark) return tradeMark;
  if (wbOwnerId !== null) return `Продавец ${wbOwnerId}`;
  return 'Кабинет без имени — не синхронизирован';
}

